import { AfterViewInit, Directive, ElementRef, inject, input, NgZone, numberAttribute, OnDestroy } from '@angular/core';

/** Bare `whFillHeight` (no value) yields '', and numberAttribute('') is NaN — which would
 * silently produce `height: NaNpx`. Fall back to the default instead. */
const px = (fallback: number) => (value: unknown) => {
  const n = numberAttribute(value, fallback);
  return Number.isNaN(n) ? fallback : n;
};

/**
 * Sizes the host so it fills whatever vertical space is left in the nearest scrolling
 * ancestor (`lib-shell`'s `main.shell-content`, which has a fixed height).
 *
 *     <div whFillHeight>…</div>
 *     <div [whFillHeight]="24">…</div>   <!-- 24px kept free underneath -->
 *
 * Why not CSS: a flex chain would need every ancestor to be a bounded flex column,
 * which would change how every sibling tab scrolls. A `calc(100vh - Npx)` needs a
 * magic N that breaks as soon as the panel above grows a row.
 *
 * The measurement is taken against the scroll container's *content*, not the viewport,
 * so scrolling never feeds back into the computed height.
 */
@Directive({
  selector: '[whFillHeight]',
  standalone: true,
})
export class FillHeightDirective implements AfterViewInit, OnDestroy {
  /** Pixels left free below the host. */
  readonly gap = input(16, { alias: 'whFillHeight', transform: px(16) });
  /** Never shrink past this — below it, the scroll container takes over. */
  readonly minHeight = input(320, { transform: px(320) });

  #el: ElementRef<HTMLElement> = inject(ElementRef);
  #zone = inject(NgZone);
  #observer?: ResizeObserver;
  #scroller: HTMLElement | null = null;
  #timers: number[] = [];

  ngAfterViewInit(): void {
    // Outside Angular: this only writes a style, and it fires on every layout change.
    this.#zone.runOutsideAngular(() => {
      const el = this.#el.nativeElement;
      this.#scroller = el.closest('.shell-content') as HTMLElement | null;
      this.#apply();

      // Content above (header, info panel) usually lands after this hook. A ResizeObserver
      // can't catch that on its own: when the panel grows, this element is *moved*, not
      // resized, and RO only reports size changes. So re-measure across the next few ticks.
      requestAnimationFrame(this.#apply);
      this.#timers.push(setTimeout(this.#apply, 250) as unknown as number);

      this.#observer = new ResizeObserver(() => this.#apply());
      // Whichever of these exists: the shell's fixed scroll box (inside) or the
      // content-height <main> (integrator). One of them does resize when the page grows.
      const main = el.closest('main') as HTMLElement | null;
      if (this.#scroller) this.#observer.observe(this.#scroller);
      if (main && main !== this.#scroller) this.#observer.observe(main);
      if (el.parentElement) this.#observer.observe(el.parentElement);
      window.addEventListener('resize', this.#apply);
    });
  }

  ngOnDestroy(): void {
    this.#observer?.disconnect();
    this.#timers.forEach(clearTimeout);
    window.removeEventListener('resize', this.#apply);
  }

  #apply = (): void => {
    const el = this.#el.nativeElement;
    const scroller = this.#scroller;
    const available = scroller ? scroller.clientHeight : window.innerHeight;
    // Offset of the host inside the scroller's content — independent of scrollTop, so
    // growing the host can't shift the measurement and loop.
    const offset = scroller
      ? el.getBoundingClientRect().top - scroller.getBoundingClientRect().top + scroller.scrollTop
      : el.getBoundingClientRect().top + window.scrollY;

    const next = Math.max(this.minHeight(), Math.round(available - offset - this.gap()));
    if (Math.abs(el.clientHeight - next) > 1) el.style.height = `${next}px`;
  };
}
